import React, {useEffect, useState} from 'react';
import {App, Button, Divider, Empty, Input, List, Popconfirm, Space, Table, Tag, Typography,} from 'antd';
import {
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    PauseCircleOutlined,
    PlusOutlined,
    RobotOutlined,
    SearchOutlined,
    TagsOutlined,
} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {agentConfigService} from '../../services/agentConfigService';
import {MemoryBlockItem, MemoryItem, memoryService, MemoryVerifyItem} from '../../services/memoryService';

const {Text, Title} = Typography;

const STATUS_MAP: Record<number, { text: string; color: string }> = {
    0: {text: '待审核', color: 'orange'},
    1: {text: '生效', color: 'green'},
    2: {text: '驳回', color: 'red'},
    3: {text: '停用', color: 'default'},
};

/**
 * 选定范围：agent = 智能体私有记忆块；block = 共享记忆块
 */
interface SelectedScope {
    type: 'agent' | 'block';
    id: number;
    name: string;
}

/**
 * 记忆池管理页：选定记忆块后管理块内记忆（私有块无需审核，共享块副本需审核）
 */
const MemoryPoolManage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [agents, setAgents] = useState<any[]>([]);
    const [blocks, setBlocks] = useState<MemoryBlockItem[]>([]);
    const [selected, setSelected] = useState<SelectedScope | null>(null);
    const [memories, setMemories] = useState<MemoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [addContent, setAddContent] = useState('');
    const [verifyQuery, setVerifyQuery] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyResults, setVerifyResults] = useState<MemoryVerifyItem[]>([]);

    useEffect(() => {
        loadScopes();
    }, []);

    useEffect(() => {
        if (selected) {
            loadMemories(selected);
        } else {
            setMemories([]);
        }
        setVerifyResults([]);
        setAddContent('');
        setVerifyQuery('');
    }, [selected]);

    const loadScopes = async () => {
        try {
            const [agentList, blockList] = await Promise.all([
                agentConfigService.getAllAgentConfigs(),
                memoryService.listBlocks(),
            ]);
            setAgents(agentList || []);
            setBlocks(blockList || []);
        } catch {
            messageApi.error('加载记忆块列表失败');
        }
    };

    const loadMemories = async (scope: SelectedScope) => {
        setLoading(true);
        try {
            const data = scope.type === 'agent'
                ? await memoryService.listPrivateMemories(scope.id)
                : await memoryService.listBlockMemories(scope.id);
            setMemories(data || []);
        } catch {
            messageApi.error('加载记忆列表失败');
        } finally {
            setLoading(false);
        }
    };

    const statusTag = (status: number) => {
        const cfg = STATUS_MAP[status] || {text: '未知', color: 'default'};
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
    };

    const handleAdd = async () => {
        if (!selected) {
            return;
        }
        if (!addContent || !addContent.trim()) {
            messageApi.warning('请输入记忆内容');
            return;
        }
        try {
            if (selected.type === 'agent') {
                await memoryService.addPrivateMemory(selected.id, addContent.trim());
            } else {
                await memoryService.addBlockMemory(selected.id, addContent.trim());
            }
            messageApi.success('添加成功');
            setAddContent('');
            await loadMemories(selected);
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '添加失败');
        }
    };

    const handleApprove = async (id: number) => {
        try {
            await memoryService.approve(id);
            messageApi.success('已通过并生效');
            if (selected) {
                await loadMemories(selected);
            }
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '操作失败');
        }
    };

    const handleReject = async (id: number) => {
        try {
            await memoryService.reject(id);
            messageApi.success('已驳回');
            if (selected) {
                await loadMemories(selected);
            }
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '操作失败');
        }
    };

    const handleDisable = async (id: number) => {
        try {
            await memoryService.disable(id);
            messageApi.success('已停用');
            if (selected) {
                await loadMemories(selected);
            }
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '操作失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await memoryService.deleteMemory(id);
            messageApi.success('删除成功');
            if (selected) {
                await loadMemories(selected);
            }
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '删除失败');
        }
    };

    const handleVerify = async () => {
        if (!selected) {
            return;
        }
        if (!verifyQuery || !verifyQuery.trim()) {
            messageApi.warning('请输入验证查询文本');
            return;
        }
        setVerifying(true);
        try {
            const req = selected.type === 'agent'
                ? {agentConfigId: selected.id, query: verifyQuery.trim()}
                : {blockId: selected.id, query: verifyQuery.trim()};
            const results = await memoryService.verify(req);
            setVerifyResults(results || []);
            if (!results || results.length === 0) {
                messageApi.info('未检索到相关记忆');
            }
        } catch (e: any) {
            messageApi.error(e?.response?.data?.message || '验证检索失败');
            setVerifyResults([]);
        } finally {
            setVerifying(false);
        }
    };

    const columns: ColumnsType<MemoryItem> = [
        {title: 'ID', dataIndex: 'id', key: 'id', width: 70},
        {
            title: '内容', dataIndex: 'content', key: 'content', ellipsis: true,
            render: (content: string) => <Text style={{maxWidth: 320, display: 'inline-block'}}
                                               ellipsis={{tooltip: content}}>{content}</Text>,
        },
        {
            title: '状态', key: 'status', width: 90,
            render: (_, r) => statusTag(r.status),
        },
        {
            title: '来源', key: 'source', width: 90,
            render: (_, r) => r.source === 1 ? <Tag color="blue">自动提取</Tag> :
                <Tag>人工添加</Tag>,
        },
        {
            title: '产生者', key: 'agentConfigId', width: 90,
            render: (_, r) => r.agentConfigId ? <Text code>#{r.agentConfigId}</Text> : '-',
        },
        {title: '创建时间', dataIndex: 'createdTime', key: 'createdTime', width: 170},
        {
            title: '操作', key: 'action', width: 170,
            render: (_, r) => (
                <Space size="small">
                    {r.status === 0 && (
                        <>
                            <Button type="text" size="small" icon={<CheckOutlined/>}
                                    onClick={() => handleApprove(r.id)}>通过</Button>
                            <Button type="text" size="small" danger icon={<CloseOutlined/>}
                                    onClick={() => handleReject(r.id)}>驳回</Button>
                        </>
                    )}
                    {r.status === 1 && (
                        <Button type="text" size="small" icon={<PauseCircleOutlined/>}
                                onClick={() => handleDisable(r.id)}>停用</Button>
                    )}
                    <Popconfirm title="确定删除该记忆？"
                                onConfirm={() => handleDelete(r.id)}
                                icon={<ExclamationCircleOutlined style={{color: 'red'}}/>}>
                        <Button type="text" size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const verifyColumns: ColumnsType<MemoryVerifyItem> = [
        {title: 'ID', dataIndex: 'memoryId', key: 'memoryId', width: 70},
        {
            title: '内容', key: 'content', ellipsis: true,
            render: (_, r) => <Text style={{maxWidth: 360, display: 'inline-block'}}
                                    ellipsis={{tooltip: r.content}}>{r.content}</Text>,
        },
        {
            title: '状态', key: 'status', width: 90,
            render: (_, r) => statusTag(r.status),
        },
        {
            title: '来源', key: 'source', width: 90,
            render: (_, r) => r.source === 1 ? <Tag color="blue">自动提取</Tag> :
                <Tag>人工添加</Tag>,
        },
        {
            title: '相似度', dataIndex: 'similarity', key: 'similarity', width: 90,
            render: (v: number) => <Text strong
                                         style={{color: v >= 0.5 ? '#52c41a' : undefined}}>{v.toFixed(4)}</Text>,
        },
    ];

    const scopeTitle = selected
        ? (selected.type === 'agent' ? `私有记忆 - ${selected.name}` : `共享块 - ${selected.name}`)
        : '请选择记忆块';

    return (
        <div style={{padding: 24}}>
            <Title level={4} style={{marginTop: 0}}>记忆池管理</Title>
            <div style={{display: 'flex', gap: 16}}>
                {/* 左侧：块选择 */}
                <div style={{width: 260, flexShrink: 0}}>
                    <Text strong>智能体私有块</Text>
                    <List
                        size="small"
                        style={{marginTop: 8, marginBottom: 16}}
                        dataSource={agents}
                        locale={{emptyText: <Empty description="暂无智能体" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}}
                        renderItem={(agent: any) => (
                            <List.Item
                                style={{
                                    cursor: 'pointer',
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    background: selected?.type === 'agent' && selected.id === agent.id
                                        ? 'rgba(24,144,255,0.12)' : undefined,
                                    borderRadius: 6,
                                }}
                                onClick={() => setSelected({type: 'agent', id: agent.id, name: agent.name})}
                            >
                                <RobotOutlined style={{marginRight: 8}}/>
                                {agent.name}
                            </List.Item>
                        )}
                    />
                    <Text strong>共享记忆块</Text>
                    <List
                        size="small"
                        style={{marginTop: 8}}
                        dataSource={blocks}
                        locale={{emptyText: <Empty description="暂无共享块" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}}
                        renderItem={(block: MemoryBlockItem) => (
                            <List.Item
                                style={{
                                    cursor: 'pointer',
                                    paddingLeft: 12,
                                    paddingRight: 12,
                                    background: selected?.type === 'block' && selected.id === block.id
                                        ? 'rgba(24,144,255,0.12)' : undefined,
                                    borderRadius: 6,
                                }}
                                onClick={() => setSelected({type: 'block', id: block.id, name: block.name})}
                            >
                                <TagsOutlined style={{marginRight: 8}}/>
                                {block.name}
                                {block.status === 0 && <Tag style={{marginLeft: 8}}>停用</Tag>}
                            </List.Item>
                        )}
                    />
                </div>

                {/* 右侧：块内记忆管理 */}
                <div style={{flex: 1, minWidth: 0}}>
                    <Title level={5} style={{marginTop: 0}}>{scopeTitle}</Title>
                    <Space.Compact style={{width: '100%', marginBottom: 16}}>
                        <Input
                            placeholder={selected?.type === 'agent'
                                ? '输入记忆内容，添加后立即生效'
                                : '输入记忆内容，添加进共享块并立即生效'}
                            value={addContent}
                            disabled={!selected}
                            onChange={(e) => setAddContent(e.target.value)}
                            onPressEnter={handleAdd}
                        />
                        <Button type="primary" icon={<PlusOutlined/>} disabled={!selected}
                                onClick={handleAdd}>添加</Button>
                    </Space.Compact>

                    <Table
                        columns={columns}
                        dataSource={memories}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        pagination={{pageSize: 10, showTotal: (t) => `共 ${t} 条`}}
                        locale={{emptyText: <Empty description="暂无记忆" image={Empty.PRESENTED_IMAGE_SIMPLE}/>}}
                    />

                    <Divider orientation="left">验证检索</Divider>
                    <Space.Compact style={{width: '100%', marginBottom: 12}}>
                        <Input
                            placeholder="输入一句话验证当前块的记忆检索效果（与执行时检索逻辑一致）"
                            value={verifyQuery}
                            disabled={!selected}
                            onChange={(e) => setVerifyQuery(e.target.value)}
                            onPressEnter={handleVerify}
                        />
                        <Button type="primary" icon={<SearchOutlined/>} loading={verifying}
                                disabled={!selected} onClick={handleVerify}>
                            验证检索
                        </Button>
                    </Space.Compact>
                    {verifyResults.length > 0 && (
                        <Table
                            columns={verifyColumns}
                            dataSource={verifyResults}
                            rowKey="memoryId"
                            size="small"
                            pagination={false}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MemoryPoolManage;
